import {
  ActionSubmit,
  Label,
  useToasterContext,
  DialogContentForm,
  DialogBody,
  DialogHeading,
  DialogCloseX,
  DialogButton,
  DialogButtons,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import escapeStringRegexp from "escape-string-regexp"
import type {
  ChangeEventHandler,
  FormEventHandler,
} from "react"
import { useCallback } from "react"
import styles from "./ChangePassword.module.scss"
import type { ChangePasswordFormState } from "./ChangePasswordFormState"
import {
  PasswordInput,
  PasswordRequirements,
} from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useAccountProperty,
  useChangePassword,
} from "src/hooks"
import {
  UpdatePasswordErrorToast,
  ChangePasswordSuccessToast,
} from "src/toasts"
import {
  passwordPattern,
  injectProps,
  getSugarmateError,
} from "src/utils"

const currentPasswordId = "password-input"
const passwordId = "new-password-input"
const passwordConfirmationId = "confirm-new-password-input"
const polyglotPrefix = "pages.account.account_details.change_password."

const passwordErrorId = "user-password-input-error"
const passwordConfirmationErrorId = "user-password-confirmation-input-error"

interface Props {
  onSave: () => void;
  formState: ChangePasswordFormState;
  setFormState: (f: ChangePasswordFormState) => void;
}

/*
 * this updates the password for a logged in user
 */
export function ChangePasswordForm(props: Props) {
  const {
    onSave,
    formState,
    setFormState,
  } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const changePassword = useChangePassword()
  const email = useAccountProperty(
    "email",
    "",
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          await changePassword(
            formState.currentPassword,
            formState.newPassword,
          )

          showToast(
            "change-password-success-toast",
            ChangePasswordSuccessToast,
          )

          onSave()
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            UpdatePasswordErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "change-password-error-toast",
            ErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      onSave,
      formState,
      changePassword,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const onCurrentPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setFormState({
        ...formState,
        currentPassword: e.target.value,
      })
    },
    [
      formState,
      setFormState,
    ],
  )

  const onNewPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setFormState({
        ...formState,
        newPassword: e.target.value,
      })
    },
    [
      formState,
      setFormState,
    ],
  )

  const onNewPasswordConfirmationChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setFormState({
        ...formState,
        newPasswordConfirmation: e.target.value,
      })
    },
    [
      formState,
      setFormState,
    ],
  )

  // this pattern will match an empty value or a value that exactly matches the password field
  const passwordConfirmationPattern = `^(${escapeStringRegexp(formState.newPassword)})?$`

  return (
    <DialogContentForm
      onSubmit={onSubmit}
      className={styles.dialogForm}
    >
      <DialogHeading>
        {polyglot.t(`${polyglotPrefix}heading`)}
        <DialogCloseX />
      </DialogHeading>
      <DialogBody className={styles.dialogBody}>
        <PasswordRequirements className={styles.passwordRequirements} />
        {/* password forms should have username fields for accessibility */}
        <input
          value={email}
          autoComplete="email"
          readOnly
          hidden
        />
        <div className={styles.inputGroup}>
          <Label
            htmlFor={currentPasswordId}
            className={styles.label}
            sizePreset="small"
          >
            {polyglot.t(`${polyglotPrefix}current_password.label`)}
          </Label>
          <PasswordInput
            id={currentPasswordId}
            placeholder={polyglot.t(`${polyglotPrefix}current_password.placeholder`)}
            className={styles.input}
            value={formState.currentPassword}
            onChange={onCurrentPasswordChange}
            autoComplete="current-password"
            required
          />
        </div>
        <div className={styles.inputGroup}>
          <Label
            htmlFor={passwordId}
            className={styles.label}
            sizePreset="small"
          >
            {polyglot.t(`${polyglotPrefix}new_password.label`)}
          </Label>
          <ValidationContainer>
            <PasswordInput
              id={passwordId}
              placeholder={polyglot.t(`${polyglotPrefix}new_password.placeholder`)}
              className={styles.input}
              value={formState.newPassword}
              onChange={onNewPasswordChange}
              pattern={passwordPattern}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <ValidationMessage
              id={passwordErrorId}
              invalidMessage={{
                valueMissing: polyglot.t("validation_message.value_missing"),
                tooShort: polyglot.t("validation_message.password_too_short"),
                patternMismatch: polyglot.t("validation_message.password_invalid"),
              }}
            />
          </ValidationContainer>
        </div>
        <div className={styles.inputGroup}>
          <Label
            htmlFor={passwordConfirmationId}
            className={styles.label}
            sizePreset="small"
          >
            {polyglot.t(`${polyglotPrefix}new_password_confirmation.label`)}
          </Label>
          <ValidationContainer>
            <PasswordInput
              id={passwordConfirmationId}
              placeholder={polyglot.t(`${polyglotPrefix}new_password_confirmation.placeholder`)}
              className={styles.input}
              value={formState.newPasswordConfirmation}
              onChange={onNewPasswordConfirmationChange}
              pattern={passwordConfirmationPattern}
              autoComplete="new-password"
              required
            />
            <ValidationMessage
              id={passwordConfirmationErrorId}
              invalidMessage={{
                valueMissing: polyglot.t("validation_message.value_missing"),
                patternMismatch: polyglot.t("validation_message.password_mismatch"),
              }}
            />
          </ValidationContainer>
        </div>
      </DialogBody>
      <DialogButtons>
        <DialogButton
          stylePreset="secondary"
          id="cancel-password-change-btn"
        >
          {polyglot.t(`${polyglotPrefix}cancel_cta`)}
        </DialogButton>
        <ActionSubmit
          id="Save"
          value={polyglot.t(`${polyglotPrefix}submit_cta`)}
          stylePreset="primary"
          className={styles.dialogMultiButton}
        />
      </DialogButtons>
    </DialogContentForm>
  )
}
